import _ from "lodash";
import React from "react";

import { CharacterCounter } from "@/component-library/CharacterCounter/CharacterCounter";
import { FormTextArea } from "@/component-library/FormFields/FormTextArea/FormTextArea";
import {
  INTERVENTION_AND_PLAN_CHARACTER_LIMIT,
  INTERVENTION_AND_PLAN_CHARACTER_LIMIT_ERROR,
} from "@/features/notes/(v2)/constants/intervention-and-plan-v2-form-schema";
import { makeCnaiProps } from "@/lib/helpers/clinically-ai";
import { Text, TextTypes } from "@/component-library";

// isCnaiEnabled: this component derives its own cnai field id from the goal, so callers can't pass a
// field descriptor in — they opt this instance in instead. Only one rendered instance per note may
// be tagged, otherwise the same cnai-template-field-id appears twice in one template container.
export function InterventionAndPlanNoteField({
  editable,
  editHandler,
  formikProps,
  goalNumber,
  isCnaiEnabled,
  isEditing,
}) {
  const hasCharacterLimitErrorAndEditable =
    editable &&
    formikProps.errors?.[goalNumber]?.interventions_and_plans ===
      INTERVENTION_AND_PLAN_CHARACTER_LIMIT_ERROR;

  if (!isEditing) {
    return (
      <>
        <Text
          className={
            hasCharacterLimitErrorAndEditable
              ? "MemberProfileGoalsAndObjectiveNoteDisplayV2--form_field_view_only_error"
              : "MemberProfileGoalsAndObjectiveNoteDisplayV2--form_field_view_only"
          }
          type={TextTypes.paragraph}
        >
          {formikProps.values[goalNumber].interventions_and_plans}
        </Text>

        {hasCharacterLimitErrorAndEditable && (
          <CharacterCounter
            characterLimit={INTERVENTION_AND_PLAN_CHARACTER_LIMIT}
            length={formikProps.values[goalNumber].interventions_and_plans.length}
          />
        )}
      </>
    );
  }

  return (
    <>
      <FormTextArea
        formikProps={formikProps}
        inputClassName="MemberProfileGoalsAndObjectiveNoteDisplayV2--form_field"
        name={`${goalNumber}.interventions_and_plans`}
        onChangeCallBack={async (name, value) => {
          const dataToSave = _.cloneDeep(formikProps.values);

          delete dataToSave["isEditingInternal"];
          dataToSave[goalNumber] = {
            ...dataToSave[goalNumber],
            interventions_and_plans: value,
          };

          editHandler("goals_objectives_progress_v2", dataToSave);
        }}
        testId="interventionAndPlanTextArea"
        textareaProps={
          isCnaiEnabled
            ? makeCnaiProps({
                id: `interventions-and-plans`,
                instructions: `Write 3-4 bullet points summarizing the distinct therapeutic interventions delivered during this session, based on the Transcript. Provide one bullet per intervention (aim for 3-4 so multiple options are available), rotating techniques - do not repeat the same modality across bullets. Format each bullet as: "- **Label:** Description of what the therapist did, the target issue/goal it addressed, and how the client responded or what was practiced." Each bullet: 20-30 words, bold label, clinically specific and varied. Tie the intervention to the specific goal, objective, or stressor it targeted (e.g., emotional regulation around medical visits, boundary-setting with family, sleep hygiene). If a session goal or objective is stated, ensure at least one bullet directly addresses it. Common labels: CBT, DBT Skills, Motivational Interviewing, Mindfulness, Strengths-Based Therapy, Behavioral Activation, Psychoeducation, Attachment-Informed Therapy, Grief-Focused Supportive Therapy, Brief Therapy / Solution-Focused, Trauma-Informed Therapy, Case Management / Care Coordination, Anger Management (CBT-based), Exposure Therapy, Supportive Therapy. Use concise, factual clinical language. Do not reference the patient by name - use "the client." Do not include symptom descriptions (those belong in Symptom Trends) and do not include follow-up scheduling or between-session homework logistics (those belong in Exploration) - focus on the intervention itself and the client's in-session response. Avoid vague phrases like "therapy was provided"; specify the technique and its target. Example: "- **Supportive Therapy:** Provided validation for client's emotional distress around medical visits and reinforced adaptive coping; client responded with increased openness and engagement." "- **Psychoeducation:** Reviewed medication changes and potential side effects/benefits, supporting client's understanding and adherence to the treatment plan." "- **Strengths-Based Therapy:** Highlighted client's resilience managing work, health, and financial stressors, fostering empowerment and gratitude for current supports."`,
                name: "Interventions & Plans",
                type: "string",
              })
            : undefined
        }
        triggerErrorOnChange
      />

      {formikProps.errors?.[goalNumber]?.interventions_and_plans ===
        INTERVENTION_AND_PLAN_CHARACTER_LIMIT_ERROR && (
        <CharacterCounter
          characterLimit={INTERVENTION_AND_PLAN_CHARACTER_LIMIT}
          length={formikProps.values[goalNumber].interventions_and_plans.length}
        />
      )}
    </>
  );
}
